import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

// PrimeVue surfaces are driven by the console's own `--color-slate-*` ramp rather than by
// fixed hex values. The console has four themes (Light Layout, Balanced Hybrid, Midnight Blue,
// Obsidian Black) but PrimeVue only knows two color schemes, selected by `.app-dark`. Hardcoded
// surfaces therefore only matched the app in whichever theme they happened to be sampled from,
// and looked wrong in the other one on that side (a stark white slab in Balanced Hybrid, a navy
// slab in Obsidian Black). Pointing both schemes at the ramp makes every PrimeVue component
// follow whatever theme class is on <html>, including themes added later, with no per-screen CSS.
//
// The polarity already lines up: light-theme/balanced-theme resolve to PrimeVue's light scheme,
// and the ramp inverts there (slate-900 is the card, slate-100 is the text), which is exactly
// how PrimeVue reads its light surface scale. See `.light-theme`, `.balanced-theme` and
// `.dim-theme` in assets/css/style.css for the per-theme values.
export const PrimeVuePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f0f5ff',
      100: '#d9e6ff',
      200: '#b8d2ff',
      300: '#87b4ff',
      400: '#4d8eff',
      500: '#1A5FD2', // Fareclock primary blue
      600: '#1049b0',
      700: '#0c378d',
      800: '#100D54', // Fareclock dark blue
      900: '#0c0a3e', // Fareclock slate 950
      950: '#060525'
    },
    colorScheme: {
      light: {
        // Light scale runs lightest -> darkest. In .light-theme / .balanced-theme the ramp is
        // inverted, so slate-900 is the card surface and slate-100 is the darkest text.
        surface: {
          0: 'var(--color-slate-900)', // card / content background
          50: 'var(--color-slate-950)', // page background; striped rows, filled inputs
          100: 'var(--color-slate-955)', // inset / hover background
          200: 'var(--color-slate-800)', // content border
          300: 'var(--color-slate-850)', // form field border
          400: 'var(--color-slate-700)', // icons, muted marks
          500: 'var(--color-slate-550)', // muted text
          600: 'var(--color-slate-555)',
          700: 'var(--color-slate-500)', // body text
          800: 'var(--color-slate-400)',
          900: 'var(--color-slate-300)',
          950: 'var(--color-slate-200)'
        }
      },
      dark: {
        // Dark scale runs the other way: PrimeVue reads surface-0 as the text color and
        // surface-900 as the content background, which matches the ramp under .app-dark.
        surface: {
          0: 'var(--color-slate-100)', // body text
          50: 'var(--color-slate-200)',
          100: 'var(--color-slate-300)',
          200: 'var(--color-slate-350)',
          300: 'var(--color-slate-455)',
          400: 'var(--color-slate-400)', // muted text
          500: 'var(--color-slate-500)',
          600: 'var(--color-slate-700)', // form field border
          700: 'var(--color-slate-800)', // content border
          800: 'var(--color-slate-855)', // inset / hover background
          900: 'var(--color-slate-900)', // card / content background
          950: 'var(--color-slate-955)' // deepest: form field background
        }
      }
    }
  }
})

/**
 * Options passed to `app.use(PrimeVue, { theme: { ... } })`.
 */
export const PrimeVueThemeOptions = {
  // This is the CSS *variable* prefix (--p-datatable-row-background), not the Tailwind utility
  // prefix. They are unrelated; keep it as 'p' so that the documented token names work, and so
  // that tailwindcss-primeui -- which hardcodes --p-* -- resolves.
  prefix: 'p',
  darkModeSelector: '.app-dark',
  cssLayer: {
    name: 'primevue',
    // Keep bootstrap ahead of primevue utilities and place tailwind utilities last.
    order: 'theme, bootstrap, bootstrap-vue, bootstrap-overrides, legacy, primevue, components, utilities'
  }
}
